import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'react-toastify';

import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import { formatDateTime } from '../../helpers/formatDateTime';

const EmployeePolicyInbox = () => {
  const { portalName } = useParams();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [acknowledged, setAcknowledged] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);

  const fetchMyPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(SummaryApi.getMyPolicies.url);
      if (res.data.success) {
        setPending(res.data.pending || []);
        setAcknowledged(res.data.acknowledged || []);
      }
    } catch {
      toast.error('Failed to load your documents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMyPolicies(); }, [fetchMyPolicies]);

  const list = tab === 'pending' ? pending : acknowledged;

  return (
    <div className="p-4">
      <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-6">My Letters & Policies</h1>

      <div className="flex gap-2 mb-4 border-b">
        <button
          onClick={() => setTab('pending')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'pending' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}
        >
          Pending ({pending.length})
        </button>
        <button
          onClick={() => setTab('acknowledged')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'acknowledged' ? 'border-primary text-primary' : 'border-transparent text-gray-500'}`}
        >
          Acknowledged ({acknowledged.length})
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading...</p>
      ) : list.length === 0 ? (
        <div className="bg-white border rounded-xl p-10 text-center">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {tab === 'pending' ? "You're all caught up — nothing pending." : 'No acknowledged documents yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((item) => (
            <button
              key={item._id}
              onClick={() => navigate(`/${portalName}/hr/letter-policy/my-policies/${item.policyDocumentId._id}`)}
              className="w-full text-left bg-white border rounded-xl p-4 flex items-center justify-between hover:border-red-300 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-red-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{item.policyDocumentId?.title}</p>
                  <p className="text-xs text-gray-500 capitalize">{item.policyDocumentId?.documentType} · v{item.versionNumber}</p>
                </div>
              </div>
              {tab === 'pending' ? (
                <span className="flex items-center gap-1 text-xs text-yellow-600"><Clock className="h-3.5 w-3.5" /> Pending</span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle2 className="h-3.5 w-3.5" /> {formatDateTime(item.acknowledgedAt, false)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmployeePolicyInbox;
